import React from 'react';
import { Button, ButtonProps } from 'semantic-ui-react';

export type CmButtonVariant = 'primary' | 'danger' | 'ghost' | 'plain';

export interface CmButtonProps {
  variant?: CmButtonVariant;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large';
  fluid?: boolean;
  children: React.ReactNode;
  ariaLabel?: string;
  title?: string;
  // Local CSS Module class for screen-specific styling (e.g. spin animation
  // on the refresh button). Local-scope only — never used to override SUI's
  // global selectors, so a future migration off SUI just drops this prop.
  className?: string;
  testId?: string;
}

function mapVariantToButtonProps(variant: CmButtonVariant): Partial<ButtonProps> {
  switch (variant) {
    case 'primary':
      return { primary: true };
    case 'danger':
      return { negative: true };
    case 'ghost':
      return { basic: true };
    case 'plain':
      return {};
  }
}

export function CmButton({
  variant = 'plain',
  onClick,
  disabled = false,
  loading = false,
  type = 'button',
  size = 'medium',
  fluid = false,
  children,
  ariaLabel,
  title,
  className,
  testId,
}: CmButtonProps): React.ReactElement {
  const variantProps = mapVariantToButtonProps(variant);
  return (
    <Button
      {...variantProps}
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      type={type}
      size={size}
      fluid={fluid}
      aria-label={ariaLabel}
      title={title}
      className={className}
      data-testid={testId}
    >
      {children}
    </Button>
  );
}

export default CmButton;
